import { Box, CircularProgress, IconButton, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import type { IEmployeeSuggestedSkill } from '@sharedInterfaces/IEmployee';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import getSuggestedSkills from '@src/APIs/graphQl/Employee/Suggestions/getSuggestedSkills';
import hideSuggestedSkill from '@src/APIs/graphQl/Employee/Suggestions/hideSuggestedSkill';
import changeMapSkill from '@src/APIs/graphQl/Skill/changeMapSkill';
import { useErrorDialog } from '@src/App/Components/Dialogs/ErrorDialog/ErrorDialog';
import SuggestedSkillRateBar from '@src/App/Components/General/FormularElements/formsControls/inputs/SuggestedSkillRateBar/SuggestedSkillRateBar';
import type { AppState } from '@store/store';
import { ExternalLink, Lightbulb } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

export default function SuggestedSkillsWidget() {
    const theme = useTheme();
    const navigate = useNavigate();
    const showErrorDialog = useErrorDialog();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeDevelopment;
    const empSkills = useSelector((state: AppState) => state.employee.skills);
    const offline = useSelector((state: AppState) => state.client.offline);

    const [suggestedSkills, setSuggestedSkills] = useState<IEmployeeSuggestedSkill[]>([]);
    const [hiddenSkills, setHiddenSkills] = useState<number[]>([]);
    const [loading, setLoading] = useState(true);

    const filterSuggestions = (suggestions: IEmployeeSuggestedSkill[]) =>
        suggestions.filter(
            (suggestion) => !empSkills.some((e) => e.id === suggestion.id) && !hiddenSkills.includes(suggestion.id),
        );

    const getAndUpdateSkills = () => {
        getSuggestedSkills((skills) => {
            setSuggestedSkills(filterSuggestions(skills));
            setLoading(false);
        });
    };

    useEffect(() => {
        getAndUpdateSkills();
    }, []);

    useEffect(() => {
        const filtered = filterSuggestions(suggestedSkills);
        setSuggestedSkills(filtered);
        if (filtered.length <= 1 && !loading) {
            getAndUpdateSkills();
        }
    }, [empSkills, hiddenSkills]);

    const handleAddSkill = (skillId: number, level: number) => {
        changeMapSkill(skillId, true, level)
            .then(() => {
                setHiddenSkills((prev) => [...prev, skillId]);
            })
            .catch((ex) => {
                showErrorDialog(ex, `Error adding skill: ${skillId}`);
            });
    };

    const handleHideSkill = (skillId: number) => {
        hideSuggestedSkill(skillId)
            .then(() => {
                setHiddenSkills((prev) => [...prev, skillId]);
            })
            .catch((ex) => {
                showErrorDialog(ex);
            });
    };

    if (loading) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: { lg: 3, md: 2, xs: 2 },
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    bgcolor: 'background.paper',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                </Box>
            </Paper>
        );
    }

    if (suggestedSkills.length === 0) {
        return null;
    }

    const topSkills = suggestedSkills.slice(0, 2);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { lg: 3, md: 2, xs: 2 },
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                bgcolor: 'background.paper',
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lightbulb size={18} color={theme.palette.primary.main} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Skill Vorschläge
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={() => navigate(`/${ELinks.PROFILE_EDIT_SKILLS_SUGGESTIONS}`)}
                    sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                    }}
                >
                    <ExternalLink size={14} />
                </IconButton>
            </Box>

            {/* Skills List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {topSkills.map((skill) => (
                    <Box
                        key={skill.id}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                mb: 1,
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {skill.title}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 2,
                                lineHeight: 1.3,
                            }}
                        >
                            {skill.description}
                        </Typography>

                        <SuggestedSkillRateBar
                            onAdd={(level) => handleAddSkill(skill.id, level)}
                            onHide={() => handleHideSkill(skill.id)}
                            disabled={offline}
                        />
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}
